import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { SelectChangeEvent, Switch, Typography, styled } from '@mui/material';
import FieldRow from 'components/FormElements/FieldRow';
import { Row } from 'components/Layout';
import TextField from 'components/FormElements/AppTextInput';
import Spacing from 'components/Spacing';
import CurrencySelector from 'components/CurrencySelector';
import { AppNumberInput, AppSimpleSelect } from 'components/FormElements';
import BasicSelect from 'further-ui/components/BasicSelect';
import {
  ForeignCurrencyCostRateCalculationType,
  FxDataFeed,
  VehicleJurisdiction,
  CommitmentType,
  CapitalContributionRule,
  ReportingPeriods,
} from 'further-types/lp/vehicle';
import { AllocationRule } from 'further-types/lp/allocations';
import {
  AllocationRuleLabels,
  ForeignCurrencyCostRateCalculationLabels,
  VehicleJurisdictionLabels,
  CommitmentTypeLabels,
  CapitalContributionRuleLabels,
  ReportingPeriodsLabels,
} from 'further-ui/labels';

const fxDataFeedOptions = Object.values(FxDataFeed).map((feed) => ({
  value: feed,
  label: feed,
}));

const allocationRuleOptions = [
  {
    value: AllocationRule.ByCommitmentAndClosingDate,
    label: AllocationRuleLabels[AllocationRule.ByCommitmentAndClosingDate],
  },
];

const foreignCurrencyCostRateCalculationOptions = Object.values(
  ForeignCurrencyCostRateCalculationType,
).map((calculation) => ({
  value: calculation,
  label: ForeignCurrencyCostRateCalculationLabels[calculation],
}));

const vehicleJurisdictionOptions = Object.values(VehicleJurisdiction).map(
  (value: VehicleJurisdiction) => ({
    value,
    label: VehicleJurisdictionLabels[value],
  }),
);

const commitmentTypeOptions = Object.values(CommitmentType).map(
  (value: CommitmentType) => ({
    value,
    label: CommitmentTypeLabels[value],
  }),
);

const reportingPeriodOptions = Object.values(ReportingPeriods).map(
  (value: ReportingPeriods) => ({
    value,
    label: ReportingPeriodsLabels[value],
  }),
);

const capitalContributionRuleOptions = Object.values(
  CapitalContributionRule,
).map((value: CapitalContributionRule) => ({
  value,
  label: CapitalContributionRuleLabels[value],
}));

const StyledAppNumberInput = styled(AppNumberInput)(() => ({ width: 110 }));

type CommitmentOption = {
  value: string;
  label: string;
};

type Props = {
  disableChangeOfCurrency: boolean;
  disableChangeOfFinancialYear: boolean;
  disableCommitmentTypeChange: boolean;
  commitmentOptions: Array<CommitmentOption>;
};

const NONE_OPTION_VALUE = 'none';

const GeneralInformation: FC<Props> = ({
  disableChangeOfCurrency,
  disableChangeOfFinancialYear,
  disableCommitmentTypeChange,
  commitmentOptions,
}) => {
  const { control, setValue, watch } = useFormContext();
  const commitmentType = watch('commitmentType');
  const capitalContributionRule = watch('capitalContributionRule');
  const baseCurrency = watch('baseCurrency');

  const handleCommitmentTypeChange = (
    event: SelectChangeEvent<unknown>,
    onChange: (value: unknown) => void,
  ) => {
    const newValue = event.target.value;
    onChange(newValue);
    if (newValue !== CommitmentType.CapitalAndLoan) {
      setValue('capitalContributionRule', null);
      setValue('capitalContributionPercentage', null);
      setValue('fixedCapitalAmount', null);
    }
  };

  return (
    <Spacing>
      <FieldRow title="Vehicle name*" centerTitle>
        <Controller
          name="vehicleName"
          control={control}
          render={({ fieldState: { error }, field: { ref, ...rest } }) => (
            <TextField
              {...rest}
              inputRef={ref}
              placeholder="e.g. Venture Fund I"
              helperText={error?.message?.toString()}
              fullWidth
            />
          )}
        />
      </FieldRow>

      <FieldRow
        title={disableChangeOfCurrency ? 'Base currency' : 'Base currency*'}
        centerTitle
        tooltipText={
          disableChangeOfCurrency
            ? 'You cannot change the base currency once transactions have been added.'
            : ''
        }
      >
        <Controller
          name="baseCurrency"
          control={control}
          render={({ fieldState: { error }, field: { ref, ...rest } }) => (
            <CurrencySelector
              {...rest}
              error={error}
              disabled={disableChangeOfCurrency}
            />
          )}
        />
      </FieldRow>

      <FieldRow
        title="Financial year end"
        centerTitle
        tooltipText={
          disableChangeOfFinancialYear
            ? 'You cannot change the financial year end once fees have been approved.'
            : ''
        }
      >
        <Controller
          name="financialYearEndDay"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <AppNumberInput
              value={value}
              onChange={onChange}
              min={1}
              max={31}
              placeholder="Day"
              helperText={error?.message?.toString()}
              error={!!error}
              disabled={disableChangeOfFinancialYear}
              style={{ width: 120 }}
            />
          )}
        />
        <Controller
          name="financialYearEndMonth"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <AppSimpleSelect
              options={[
                { value: '1', label: 'Jan' },
                { value: '2', label: 'Feb' },
                { value: '3', label: 'Mar' },
                { value: '4', label: 'Apr' },
                { value: '5', label: 'May' },
                { value: '6', label: 'Jun' },
                { value: '7', label: 'Jul' },
                { value: '8', label: 'Aug' },
                { value: '9', label: 'Sep' },
                { value: '10', label: 'Oct' },
                { value: '11', label: 'Nov' },
                { value: '12', label: 'Dec' },
              ]}
              error={!!error}
              value={value != null ? String(value) : ''}
              onChange={(e) => {
                const numValue = Number(e.target.value);
                onChange(numValue);
              }}
              disabled={disableChangeOfFinancialYear}
              style={{ width: 140, marginLeft: 12 }}
            />
          )}
        />
      </FieldRow>

      <FieldRow title="Reporting periods" centerTitle>
        <Controller
          name="reportingPeriods"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <AppSimpleSelect
              options={reportingPeriodOptions}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              error={!!error}
              fullWidth
            />
          )}
        />
      </FieldRow>

      <FieldRow
        title="Reporting deadline"
        tooltipText="ILPA guidelines recommend that draft unaudited financials are typically delivered to investors 45–60 days after quarter-end. Fund of funds vehicles may take up to 75–90 days."
        centerTitle
      >
        <Controller
          name="reportingDeadlineDays"
          control={control}
          defaultValue={45}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Row centered spacing="sm">
              <StyledAppNumberInput
                value={value}
                onChange={onChange}
                min={1}
                max={90}
                placeholder="Days"
                helperText={error?.message?.toString()}
                error={!!error}
              />
              <Typography variant="body2"> days after period end</Typography>
            </Row>
          )}
        />
      </FieldRow>

      <FieldRow title="Vehicle jurisdiction" centerTitle>
        <Controller
          name="vehicleJurisdiction"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <AppSimpleSelect
              options={vehicleJurisdictionOptions}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              error={!!error}
              fullWidth
            />
          )}
        />
      </FieldRow>

      <FieldRow
        title="Commitment types"
        centerTitle
        tooltipText={
          disableCommitmentTypeChange
            ? 'You cannot change the commitment type after the first close.'
            : ''
        }
      >
        <Controller
          name="commitmentType"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <AppSimpleSelect
              options={commitmentTypeOptions}
              value={value}
              onChange={(e) => handleCommitmentTypeChange(e, onChange)}
              error={!!error}
              fullWidth
              disabled={disableCommitmentTypeChange}
            />
          )}
        />
      </FieldRow>

      {commitmentType === CommitmentType.CapitalAndLoan && (
        <FieldRow
          title="Capital contribution rule"
          centerTitle
          tooltipText={
            disableCommitmentTypeChange
              ? 'You cannot change the capital contribution rule after the first close.'
              : ''
          }
        >
          <Controller
            name="capitalContributionRule"
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <AppSimpleSelect
                options={capitalContributionRuleOptions}
                value={value}
                onChange={(e) => {
                  onChange(e.target.value);
                  setValue('capitalContributionPercentage', null);
                  setValue('fixedCapitalAmount', null);
                }}
                disabled={disableCommitmentTypeChange}
                error={!!error}
                errorMessage={error?.message?.toString()}
                style={{ width: 450 }}
              />
            )}
          />
          {capitalContributionRule ===
            CapitalContributionRule.FixedCommitmentPercentage && (
            <Controller
              name="capitalContributionPercentage"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <AppNumberInput
                  value={value}
                  onChange={onChange}
                  min={0}
                  max={100}
                  decimals={10}
                  minFractionDigits={2}
                  helperText={error?.message?.toString()}
                  error={!!error}
                  style={{ width: 140, marginLeft: 12 }}
                  percentEndAdornment
                  disabled={disableCommitmentTypeChange}
                />
              )}
            />
          )}

          {capitalContributionRule ===
            CapitalContributionRule.ProRataOfFixedPartnershipCapital && (
            <Controller
              name="fixedCapitalAmount"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <AppNumberInput
                  value={value}
                  onChange={onChange}
                  min={0}
                  decimals={2}
                  minFractionDigits={2}
                  helperText={error?.message?.toString()}
                  error={!!error}
                  style={{ width: 140, marginLeft: 12 }}
                  currencyStartAdornment
                  currency={baseCurrency}
                  disabled={disableCommitmentTypeChange}
                />
              )}
            />
          )}
        </FieldRow>
      )}

      <FieldRow title="Currency feed" centerTitle>
        <Controller
          name="fxDataFeed"
          control={control}
          render={({ fieldState: { error }, field: { value, onChange } }) => (
            <AppSimpleSelect
              options={fxDataFeedOptions}
              fullWidth
              error={!!error}
              value={value}
              onChange={(e) => onChange(e.target.value as FxDataFeed)}
            />
          )}
        />
      </FieldRow>
      <FieldRow title="Default allocation rule" centerTitle>
        <Controller
          name="defaultAllocationRule"
          control={control}
          render={({ fieldState: { error }, field: { value, onChange } }) => (
            <AppSimpleSelect
              options={allocationRuleOptions}
              fullWidth
              error={!!error}
              value={value}
              onChange={(e) => onChange(e.target.value as AllocationRule)}
            />
          )}
        />
      </FieldRow>
      <FieldRow title="Foreign currency cost rate calculation" centerTitle>
        <Controller
          name="foreignCurrencyCostRateCalculation"
          control={control}
          render={({ fieldState: { error }, field: { value, onChange } }) => (
            <AppSimpleSelect
              options={foreignCurrencyCostRateCalculationOptions}
              fullWidth
              error={!!error}
              value={value}
              onChange={(e) =>
                onChange(
                  e.target.value as ForeignCurrencyCostRateCalculationType,
                )
              }
            />
          )}
        />
      </FieldRow>
      <FieldRow
        title="Exclude general partner from vehicle allocations"
        centerTitle
        tooltipText="Turn this on when the general partner entity's fund commitment is to be excluded from the vehicle's total commitment and all transaction allocations."
      >
        <Controller
          name="excludeGeneralPartnerFromAllocations"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Switch
              checked={value ?? false}
              onChange={(e) => onChange(e.target.checked)}
            />
          )}
        />
      </FieldRow>
      <FieldRow
        title="Balancing entity"
        centerTitle
        tooltipText="Where currency transaction allocations are rounded to two decimal places, there may be stray cents/pennies that need allocating. By default, the system adds/deducts one cent/penny from each investor in order from largest to smallest. Setting a balancing entity will instead put these adjustments into the balancing entity only."
      >
        <Controller
          name="balancingEntityId"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <BasicSelect
              options={[
                { value: NONE_OPTION_VALUE, label: 'None' },
                ...commitmentOptions,
              ]}
              value={value ?? NONE_OPTION_VALUE}
              onChange={(selected) =>
                onChange(selected === NONE_OPTION_VALUE ? null : selected)
              }
              placeholder="Select balancing entity"
              error={!!error}
              helperText={error?.message?.toString()}
              fullWidth
            />
          )}
        />
      </FieldRow>
    </Spacing>
  );
};

export default GeneralInformation;
